import { Component, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { GuardsCheckEnd, NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { SettingsService, User } from '@delon/theme';
import { LayoutDefaultModule, LayoutDefaultOptions } from '@delon/theme/layout-default';
import { SettingDrawerModule } from '@delon/theme/setting-drawer';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';

import { AsyncPipe, CommonModule, DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoadingService } from 'projects/bo/src/app/core/services/helpers/loading.service';
import { debounceTime, filter, fromEvent } from 'rxjs';
import { AuthService } from '../../core/services/implements/auth.service';
import { HeaderClientComponent } from './widgets/client.component';
import { HeaderFarmComponent } from './widgets/farm.component';
import { HeaderNotifyComponent } from './widgets/notify.component';
import { HeaderUserComponent } from './widgets/user.component';

const SIDEBAR_FLYOUT_VISIBLE_SELECTOR = '.sidebar-nav__floating.sidebar-nav__floating-show';
const SIDEBAR_FLYOUT_VISIBLE_CLASS = 'sidebar-nav__floating-show';
const SIDEBAR_KEEP_OPEN_SELECTOR = '.sidebar-nav__item-link, .sidebar-nav__floating';
const SIDEBAR_FLYOUT_CLOSE_DEBOUNCE_MS = 150;

@Component({
  selector: 'layout-basic',
  template: `
    <div *ngIf="loader | async" class="global-spinner-overlay">
      <nz-spin nzSize="large" nzTip="Loading..." style="width:100%" />
    </div>
    <layout-default [options]="options" [asideUser]="asideUserTpl" [content]="contentTpl" [customError]="null">
      <layout-default-header-item direction="right" hidden="mobile">
        <header-client />
      </layout-default-header-item>
      <layout-default-header-item direction="right">
        <header-notify />
      </layout-default-header-item>
      <layout-default-header-item direction="right" hidden="mobile">
        <header-farm />
      </layout-default-header-item>
      <layout-default-header-item direction="right">
        <header-user />
      </layout-default-header-item>
      <ng-template #asideUserTpl>
        <div nz-dropdown class="alain-default__aside-user">
          <nz-avatar class="alain-default__aside-user-avatar" [nzSrc]="user.avatar" />
          <div class="alain-default__aside-user-info">
            <span nz-tooltip nzTooltipTitle="{{ user.name }}">
              <strong>{{ user.name }}</strong>
            </span>
            <p class="mb0" style="overflow: hidden; text-overflow:ellipsis;">
              <span nz-tooltip nzTooltipTitle="{{ user.email }}">
                {{ user.email }}
              </span>
            </p>
          </div>
        </div>

        <div class="aside-dropdown hidden-pc">
          <header-client />
        </div>
        <div class="aside-dropdown hidden-pc">
          <header-farm />
        </div>
      </ng-template>
      <ng-template #contentTpl>
        <router-outlet />
      </ng-template>
    </layout-default>
  `,
  imports: [
    CommonModule,
    RouterOutlet,
    AsyncPipe,
    LayoutDefaultModule,
    NzIconModule,
    NzMenuModule,
    NzDropDownModule,
    NzAvatarModule,
    NzSelectModule,
    NzSpinModule,
    SettingDrawerModule,
    HeaderNotifyComponent,
    HeaderUserComponent,
    HeaderFarmComponent,
    HeaderClientComponent,
    NzToolTipModule
  ]
})
export class LayoutBasicComponent implements OnInit {
  readonly authService = inject(AuthService);
  readonly loader = inject(LoadingService).get();
  private readonly settings = inject(SettingsService);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  readonly options: LayoutDefaultOptions = {
    logoExpanded: `./assets/icon/logo_expand.png`,
    logoCollapsed: `./assets/icon/logo_collapse.png`
  };
  searchToggleStatus = false;
  showSettingDrawer = false;

  get user(): User {
    return this.settings.user;
  }

  //#region show loading overlay when navigate
  protected readonly router = inject(Router);
  protected readonly loading = inject(LoadingService);
  protected readonly destroyRef = inject(DestroyRef);
  ngOnInit() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(evt => {
      if (evt instanceof GuardsCheckEnd && evt.shouldActivate) {
        this.loading.show('Menu'); // show overlay
      }
      if (evt instanceof NavigationEnd || evt instanceof NavigationCancel || evt instanceof NavigationError) {
        setTimeout(() => this.loading.hide('Menu'), 100); // hide overlay
      }
    });

    const hasOpenSidebarFlyout = () => this.document.querySelector(SIDEBAR_FLYOUT_VISIBLE_SELECTOR) !== null;

    this.ngZone.runOutsideAngular(() => {
      fromEvent<MouseEvent>(this.document, 'mousemove')
        .pipe(filter(hasOpenSidebarFlyout), debounceTime(SIDEBAR_FLYOUT_CLOSE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          if (!hasOpenSidebarFlyout()) return;
          const target = event.target as Element | null;
          if (target?.closest(SIDEBAR_KEEP_OPEN_SELECTOR)) return;
          this.document
            .querySelectorAll(SIDEBAR_FLYOUT_VISIBLE_SELECTOR)
            .forEach(node => node.classList.remove(SIDEBAR_FLYOUT_VISIBLE_CLASS));
        });
    });
  }
  //#endregion show loading overlay when navigate
}
